import React, { useEffect, useState, useCallback } from 'react'

import { HeadingSmall, Collapse, FadeIn, Loader, Spinner, Panel, Label } from '@pb/components'
import { differenceInCalendarDays } from 'date-fns'
import PropTypes from 'prop-types'
import { path } from 'ramda'

import SecondaryAction from 'ui/buttons/SecondaryAction'

import { MONTHLY_ORDER_LIST_INITIAL_SIZE } from '../../duck/constants'
import MonthlyInvoiceCard from '../MonthlyInvoiceCard'
import MonthlyInvoiceDetails from '../MonthlyInvoiceDetails'
import Order from '../Order'
import styles from './MonthlyInvoice.scss'

const MonthlyInvoiceComponent: React.FC<any> = ({
  invoice,
  invoiceId,
  invoiceOrderIds,
  expandMonthlyInvoiceOrderById,
  expandMonthlyInvoiceById,
  expandedOrderId,
  expandedMonthlyInvoiceId,
  isInvoiceOrdersLoaded,
  isMonthlyInvoiceOrdersLoading,
  fetchMonthlyInvoiceOrders,
  shouldShowLoadMoreButton,
  loadingOrderId,
  isLoadingMore,
  loadMoreOrders,
  runKlarnaOrderOperation,
  filterName,
}) => {
  const [isMonthlyInvoiceExpanded, setIsMonthlyInvoiceExpanded] = useState(false)

  useEffect(() => {
    setIsMonthlyInvoiceExpanded(invoiceId === expandedMonthlyInvoiceId)
  }, [invoiceId, expandedMonthlyInvoiceId])

  useEffect(() => {
    if (isMonthlyInvoiceExpanded && !isMonthlyInvoiceOrdersLoading && !isInvoiceOrdersLoaded) {
      fetchMonthlyInvoiceOrders(invoice.collection_id)
    }
  }, [isMonthlyInvoiceExpanded])

  const handleClickOnMonthlyInvoice = useCallback(() => {
    expandMonthlyInvoiceById(invoiceId)

    if (!isInvoiceOrdersLoaded) {
      fetchMonthlyInvoiceOrders(invoice.collection_id)
    }
  }, [invoiceId, invoice])

  const monthlyInvoiceDetails = {
    amountLeftToPay: path(['payment_details', 'amount_left_to_pay'], invoice),
    servicingCharges: path(['payment_details', 'servicing_charges'], invoice),
    dueDate: path(['payment_details', 'due_date'], invoice),
  }

  const isAfterMonthlyInvoiceEndDate = differenceInCalendarDays(new Date(), invoice.end_date) >= 0

  const ordersList = (): any =>
    isInvoiceOrdersLoaded ? (
      <>
        <FadeIn>
          <HeadingSmall className={styles.ordersListHeading}>
            <Label
              copyKey={
                isAfterMonthlyInvoiceEndDate
                  ? 'omni.details.todo.section.purchases.section.heading'
                  : 'omni.details.allinvoices.section.purchases.section.heading'
              }
            />
          </HeadingSmall>
        </FadeIn>
        <ul>
          {invoiceOrderIds.map((orderId, i) => (
            <Order
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ className: string; key: any; orderId: any;... Remove this comment to see the full error message
              className={styles.monthlyInvoiceOrder}
              key={filterName + orderId}
              orderId={orderId}
              iterator={i % MONTHLY_ORDER_LIST_INITIAL_SIZE}
              expandedOrderId={expandedOrderId}
              toggleExpandedOrder={() => expandMonthlyInvoiceOrderById(orderId)}
              loadingOrderId={loadingOrderId}
              runKlarnaOp={runKlarnaOrderOperation}
            />
          ))}
        </ul>
      </>
    ) : (
      <Loader />
    )

  const loadMoreButton = (): any =>
    shouldShowLoadMoreButton && (
      <Panel padding="m m m none">
        <FadeIn delay={75 * invoiceOrderIds.length}>
          <SecondaryAction
            size="large"
            fullWidth
            onClick={() => loadMoreOrders(invoiceId)}
            className={styles.loadMoreButton}
            disabled={isLoadingMore}
          >
            <Spinner isRendered={isLoadingMore} containerClassName={styles.spinner} />
            {!isLoadingMore && <Label copyKey="account.digitalreceipts.loadmore" />}
          </SecondaryAction>
        </FadeIn>
      </Panel>
    )

  return (
    <li className={`${styles.container} ${isMonthlyInvoiceExpanded ? styles.isExpanded : ''}`}>
      <Collapse
        isExpanded={isMonthlyInvoiceExpanded}
        toggleClassNames={[styles.toggle]}
        toggleContent={(isExpanded) => (
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ isExpanded: boolean; invoiceId: any; }' is... Remove this comment to see the full error message
          <MonthlyInvoiceCard isExpanded={isExpanded} invoiceId={invoiceId} />
        )}
        controlled
        onClick={handleClickOnMonthlyInvoice}
        buttonTestID="monthlyInvoiceButton"
      >
        <>
          {ordersList()}
          {loadMoreButton()}
          <FadeIn delay={75 * (invoiceOrderIds.length + 1)}>
            <Panel padding="m">
              <MonthlyInvoiceDetails {...monthlyInvoiceDetails} />
            </Panel>
          </FadeIn>
        </>
      </Collapse>
    </li>
  )
}

MonthlyInvoiceComponent.propTypes = {
  invoice: PropTypes.object.isRequired,
  isInvoiceOrdersLoaded: PropTypes.bool,
  invoiceOrderIds: PropTypes.array.isRequired,
  shouldShowLoadMoreButton: PropTypes.bool.isRequired,
  loadingOrderId: PropTypes.string.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  expandMonthlyInvoiceById: PropTypes.func.isRequired,
  expandMonthlyInvoiceOrderById: PropTypes.func.isRequired,
  loadMoreOrders: PropTypes.func.isRequired,
  fetchMonthlyInvoiceOrders: PropTypes.func.isRequired,
  runKlarnaOrderOperation: PropTypes.func.isRequired,
  filterName: PropTypes.string,
}

export default MonthlyInvoiceComponent
